import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mic, Volume2, Loader2, Upload, Copy, CheckCircle2 } from "lucide-react";
import { API } from "./utils";
import { adminHeaders } from "../../lib/api";
import { debugLog } from "../../lib/logger";
import { T } from "@/locales/tr";

export default function VoiceTab({ pwd }) {
  const [status, setStatus] = useState(null);
  const [text, setText] = useState("Salaam, muslim. Force tranquille, paix mondiale. 🌹 — Papicoo");
  const [voiceId, setVoiceId] = useState("");
  const [playing, setPlaying] = useState(false);
  const [loading, setLoading] = useState(false);
  const audioRef = useRef(null);

  // Voice cloning state
  const [cloneName, setCloneName] = useState("Papicoo Original");
  const [cloneDesc, setCloneDesc] = useState("Voix présidentielle calme, lecture coranique");
  const [cloneFiles, setCloneFiles] = useState([]);
  const [cloning, setCloning] = useState(false);
  const [cloneResult, setCloneResult] = useState(null);

  // Voices list
  const [voices, setVoices] = useState([]);
  const [loadingVoices, setLoadingVoices] = useState(false);

  useEffect(() => {
    axios.get(`${API}/voice/status`).then((r) => {
      setStatus(r.data);
      setVoiceId(r.data.voice_id || "");
    }).catch(() => setStatus({ enabled: false }));
  }, []);

  const loadVoices = async () => {
    setLoadingVoices(true);
    try {
      const { data } = await axios.get(`${API}/admin/voice/list`, { headers: { "X-Admin-Password": pwd } });
      setVoices(data.voices || []);
      if (!voices.length) toast.success(`${data.voices?.length || 0} voix chargées.`);
    } catch (e) {
      if (e?.response?.status === 403) toast.error(e.response.data.detail);
      else toast.error(T("Échec chargement voix."));
    } finally { setLoadingVoices(false); }
  };

  const test = async () => {
    if (!status?.enabled) {
      toast.error(T("Configure d'abord ELEVENLABS_API_KEY dans Env Variables."));
      return;
    }
    if (!text.trim()) { toast.error(T("Tape un texte à dire.")); return; }
    setLoading(true);
    try {
      const payload = { text };
      if (voiceId.trim()) payload.voice_id = voiceId.trim();
      const res = await axios.post(`${API}/voice/synthesize`, payload, { responseType: "blob", headers: adminHeaders() });
      const url = URL.createObjectURL(res.data);
      const a = new Audio(url);
      audioRef.current?.pause();
      audioRef.current = a;
      a.onended = () => { setPlaying(false); URL.revokeObjectURL(url); };
      a.onerror = () => { setPlaying(false); URL.revokeObjectURL(url); };
      await a.play();
      setPlaying(true);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        // Try to read the JSON detail from the blob
        try {
          const txt = await e.response.data.text();
          const j = JSON.parse(txt);
          toast.error(j.detail || "Permission ElevenLabs insuffisante.");
        } catch { toast.error("Permission ElevenLabs insuffisante."); }
      } else if (status === 503) toast.error(T("Mode vocal non configuré."));
      else toast.error(T("Échec synthèse."));
    } finally { setLoading(false); }
  };

  const stop = () => { audioRef.current?.pause(); setPlaying(false); };

  const handleClone = async () => {
    if (!cloneFiles.length) { toast.error(T("Sélectionne 1 à 5 fichiers audio (mp3, wav, m4a)")); return; }
    if (!cloneName.trim()) { toast.error(T("Nom de la voix requis")); return; }
    setCloning(true);
    try {
      const fd = new FormData();
      fd.append("name", cloneName);
      if (cloneDesc) fd.append("description", cloneDesc);
      for (const f of cloneFiles) fd.append("files", f);
      const { data } = await axios.post(`${API}/admin/voice/clone`, fd, {
        headers: { "X-Admin-Password": pwd, "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setCloneResult(data);
      setVoiceId(data.voice_id);
      toast.success(`Voix clonée : ${T(data.name)} 🌹`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || T("Clone échoué."));
    } finally { setCloning(false); }
  };

  const copyVoiceId = async (vid) => {
    try { await navigator.clipboard.writeText(vid); toast.success(T("voice_id copié !")); }
    catch (e) { debugLog("clipboard copy failed:", e?.message); toast.info(T("Sélectionne et copie manuellement le voice_id.")); }
  };

  return (
    <div data-testid="admin-voice-tab" className="space-y-6">
      {/* --- Test live de la voix --- */}
      <div className="grid lg:grid-cols-[1fr_1.2fr] gap-6">
        <div className="papicoo-card p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">
            <Mic size={14} className="text-[color:var(--papicoo-gold)]" />
            {T("Voix Papicoo · ElevenLabs")}
          </div>
          <div className="mt-3 papicoo-card bg-[color:var(--papicoo-sand-deep)]/60 p-4 text-sm space-y-2">
            <div className="flex items-center gap-2">
              <span className={`inline-block w-2 h-2 rounded-full ${status?.enabled ? "bg-emerald-500" : "bg-amber-500"}`} />
              <strong>{T("Statut :")}</strong> {status === null ? "…" : status.enabled ? T("Activé en backend") : T("Non configuré")}
            </div>
            <div className="text-xs text-[color:var(--papicoo-muted)] break-all">
              <strong>{T("Voice ID actuelle :")}</strong> {status?.voice_id || "—"}
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Voice ID à tester (optionnelle)")}</label>
            <input
              data-testid="voice-id-input"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder={T("JBFqnCBsd6RMkjVDRZzb")}
              className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm font-mono"
            />
            <label className="block text-[11px] uppercase tracking-[0.2em] text-[color:var(--papicoo-muted)]">{T("Texte à prononcer")}</label>
            <textarea
              data-testid="voice-text-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={4}
              className="w-full bg-[color:var(--papicoo-sand)] border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
            />
            <div className="flex gap-2">
              <button
                data-testid="voice-test-btn"
                onClick={test}
                disabled={loading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full papicoo-btn-primary text-sm disabled:opacity-50"
              >
                {loading ? <Loader2 size={14} className="animate-spin" /> : <Volume2 size={14} />}
                {loading ? T("Synthèse…") : T("Écouter")}
              </button>
              {playing && (
                <button onClick={stop} data-testid="voice-stop-btn" className="px-5 py-2.5 rounded-full bg-white border border-[color:var(--papicoo-border)] text-sm">
                  {T("Arrêter")}
                </button>
              )}
            </div>
          </div>
        </div>

        {/* --- Clone ta voix --- */}
        <div className="papicoo-card p-5">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)] mb-3">
            <Upload size={14} className="text-[color:var(--papicoo-gold)]" /> {T("Clone ta voix Papicoo (Instant Clone)")}
          </div>
          <p className="text-xs text-[color:var(--papicoo-muted)] mb-3 leading-relaxed">
            {T("Upload 1 à 5 fichiers audio clairs (mp3/wav/m4a, 30s à 5 min chacun, sans bruit ni musique). ElevenLabs analyse ton timbre et crée un")} <em>{T("voice_id")}</em> {T("que tu pourras utiliser partout.")}
          </p>
          <div className="space-y-3">
            <input
              data-testid="clone-name"
              type="text"
              value={cloneName}
              onChange={(e) => setCloneName(e.target.value)}
              placeholder={T("Nom (ex: Papicoo Original)")}
              className="w-full bg-white border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
            />
            <input
              data-testid="clone-desc"
              type="text"
              value={cloneDesc}
              onChange={(e) => setCloneDesc(e.target.value)}
              placeholder={T("Description (optionnel)")}
              className="w-full bg-white border border-[color:var(--papicoo-border)] rounded-lg p-2.5 text-sm"
            />
            <input
              data-testid="clone-files"
              type="file"
              accept="audio/*"
              multiple
              onChange={(e) => setCloneFiles(Array.from(e.target.files || []))}
              className="w-full text-xs text-[color:var(--papicoo-muted)] file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[color:var(--papicoo-ink)] file:text-[color:var(--papicoo-sand)] file:cursor-pointer"
            />
            {cloneFiles.length > 0 && (
              <div className="text-[11px] text-[color:var(--papicoo-muted)]">
                {cloneFiles.length} fichier(s) sélectionné(s) — {(cloneFiles.reduce((s, f) => s + f.size, 0) / 1024 / 1024).toFixed(1)} MB
              </div>
            )}
            <button
              data-testid="clone-submit"
              onClick={handleClone}
              disabled={cloning || !cloneFiles.length}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full papicoo-btn-gold text-sm disabled:opacity-50"
            >
              {cloning ? <Loader2 size={14} className="animate-spin" /> : <Mic size={14} />}
              {cloning ? T("Clonage en cours (30-60s)…") : "Cloner ma voix"}
            </button>
            {cloneResult && (
              <div className="papicoo-card p-4 bg-emerald-50 border-l-4 border-emerald-500 text-sm space-y-2">
                <div className="inline-flex items-center gap-2 font-medium text-emerald-700">
                  <CheckCircle2 size={16} /> {T(cloneResult.name)} clonée !
                </div>
                <div className="flex items-center gap-2 text-xs">
                  <code className="font-mono text-[color:var(--papicoo-ink)] bg-white px-2 py-1 rounded">{cloneResult.voice_id}</code>
                  <button onClick={() => copyVoiceId(cloneResult.voice_id)} className="text-xs underline text-[color:var(--papicoo-accent)] inline-flex items-center gap-1">
                    <Copy size={11} /> {T("Copier")}
                  </button>
                </div>
                <p className="text-[11px] italic text-[color:var(--papicoo-muted)]">{T(cloneResult.hint)}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* --- Bibliothèque de voix --- */}
      <div className="papicoo-card p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-muted)]">{T("Bibliothèque de voix sur ton compte")}</div>
          <button onClick={loadVoices} disabled={loadingVoices} className="text-xs px-3 py-1.5 rounded-full bg-white border border-[color:var(--papicoo-border)] inline-flex items-center gap-1">
            {loadingVoices ? <Loader2 size={12} className="animate-spin" /> : "Charger"} les voix
          </button>
        </div>
        {voices.length > 0 ? (
          <div className="grid sm:grid-cols-2 gap-2">
            {voices.map((v) => (
              <div key={v.voice_id} className="flex items-center justify-between p-3 rounded-lg border border-[color:var(--papicoo-border)] bg-white hover:bg-[color:var(--papicoo-sand-deep)]/30 transition">
                <div className="min-w-0">
                  <div className="font-medium text-sm truncate">{T(v.name)}</div>
                  <div className="text-[10px] text-[color:var(--papicoo-muted)] font-mono truncate">{v.voice_id}</div>
                  {v.category && <span className="inline-block mt-1 text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-[color:var(--papicoo-gold)]/20">{v.category}</span>}
                </div>
                <button onClick={() => { setVoiceId(v.voice_id); copyVoiceId(v.voice_id); }} className="text-xs px-2 py-1 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] shrink-0">
                  {T("Utiliser")}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-xs italic text-[color:var(--papicoo-muted)] text-center py-4">
            {T("Clique « Charger les voix » pour voir tes voix ElevenLabs.")}
          </div>
        )}
      </div>
    </div>
  );
}
